import { supabase } from "@/integrations/supabase/client";
import type { RealtimeChannel } from "@supabase/supabase-js";

/**
 * Ref-counted shared postgres_changes subscription.
 *
 * Multiple components/hooks asking for the same (table, event, filter)
 * combination share a single Supabase Realtime channel. The channel is only
 * created on the first subscriber and torn down when the last unsubscribes.
 *
 * This prevents the runtime error:
 *   "cannot add `postgres_changes` callbacks for realtime:... after `subscribe()`"
 * which happens when two hook instances try to attach a `.on()` callback to a
 * channel of the same name after one has already called `.subscribe()`.
 */

type Event = "INSERT" | "UPDATE" | "DELETE" | "*";

interface SubscribeOptions {
  table: string;
  event?: Event;
  schema?: string;
  filter?: string; // e.g. `user_id=eq.${userId}`
}

interface Entry {
  channel: RealtimeChannel;
  listeners: Set<(payload: any) => void>;
}

const registry = new Map<string, Entry>();

function keyFor(opts: SubscribeOptions): string {
  return [
    opts.schema ?? "public",
    opts.table,
    opts.event ?? "*",
    opts.filter ?? "",
  ].join("|");
}

export function subscribeToTable(
  opts: SubscribeOptions,
  callback: (payload: any) => void
): () => void {
  const key = keyFor(opts);
  let entry = registry.get(key);

  if (!entry) {
    const listeners = new Set<(payload: any) => void>();
    const channel = supabase
      .channel(`shared:${key}`)
      .on(
        "postgres_changes" as any,
        {
          event: opts.event ?? "*",
          schema: opts.schema ?? "public",
          table: opts.table,
          ...(opts.filter && { filter: opts.filter }),
        },
        (payload) => {
          listeners.forEach((cb) => {
            try {
              cb(payload);
            } catch (e) {
              console.error("[realtime] listener error:", e);
            }
          });
        }
      )
      .subscribe();
    entry = { channel, listeners };
    registry.set(key, entry);
  }

  entry.listeners.add(callback);

  return () => {
    const current = registry.get(key);
    if (!current) return;
    current.listeners.delete(callback);
    if (current.listeners.size === 0) {
      supabase.removeChannel(current.channel);
      registry.delete(key);
    }
  };
}
